import { useEffect, useState } from "react";
import Toaster, { ToastConfig } from "../../components/Toaster";
import { ErrorTypes } from "../../interfaces/errors";
import {
  formatDateTask,
  formatDateTime,
  formatDateTimeShort,
} from "../../helper/dateFormatter";
import { FilterCriteria } from "tasks-experience-client";
import {
  CellClickedEvent,
  ColDef,
  ValueFormatterParams,
  ValueGetterParams,
} from "ag-grid-community";
import { GenericGrid } from "../../grid/GenericGrid";

import { ReactComponent as EditIcon } from "../../assets/img/EditPencil.svg";
import {
  EpisodeHistoryDTO,
  GridDataRequest,
  GridDataResponse,
} from "patients-experience-client";
import "../../assets/scss/custom/_custom.scss";
import { createUsersApi } from "../../api-helpers/UserApiHelper";
import { CommandCenterHeader } from "./CommandCenterHeader";
import { createGridApi } from "../../api-helpers/GridApiHelper";
import { CommandCenterConstants } from "../../constants/Lookups";
import OffCanvas from "../../components/offCanvas";
import { AddEditAlert } from "./AddEditAlert";
import { createClinicalAlertsApi } from "../../api-helpers/PatientApiHelper";
import { Modal } from "react-bootstrap";
import { UserName } from "../../components/UserName";
import { RootState } from "../../store";
import { useDispatch, useSelector } from "react-redux";

export interface Props {
  patientId?: string;
  breadcrumbs?: boolean | undefined;
}

export const episodeStatusColorMap: Record<string, string> = {
  [CommandCenterConstants.EPISODE_STATUS_NEEDS_ACTION_NEW]:
    "status-yellow-alert",
  [CommandCenterConstants.EPISODE_STATUS_EXTERNAL_PENDING]: "status-draft",
  [CommandCenterConstants.EPISODE_STATUS_INTERNAL_PENDING]: "status-draft",
  [CommandCenterConstants.EPISODE_STATUS_INTERNAL_CLOSED]: "bg-success",
  [CommandCenterConstants.EPISODE_STATUS_EXTERNAL_CLOSED]: "bg-success",
  [CommandCenterConstants.EPISODE_STATUS_OTHER_CLOSED]: "bg-success",
  [CommandCenterConstants.EPISODE_STATUS_MISSING_INFORMATION_NEW]:
    "status-yellow-alert",
  [CommandCenterConstants.EPISODE_STATUS_GENERAL_ACTIVE]: "status-red-alert",
  [CommandCenterConstants.EPISODE_STATUS_DEVICE_NEEDED_ONHOLD]: "bg-light",
  [CommandCenterConstants.EPISODE_STATUS_OTHER_ONHOLD]: "bg-light",
};

export const CommandCenterGrid = ({ patientId, breadcrumbs }: Props) => {
  const [filters, setFilters] = useState<FilterCriteria[]>([]);
  const [episodeHistoryData, setEpisodeHistoryData] = useState<
    EpisodeHistoryDTO[]
  >([]);
  const [isUserCacheLoaded, setIsUserCacheLoaded] = useState(false);
  const [initialFiltersReady, setInitialFiltersReady] = useState(false);
  const [selectedAlertId, setSelectedAlertId] = useState<string>("");
  const [selectedAlertPatientId, setSelectedAlertPatientId] =
    useState<string>("");
  const [gridReloadKey, setGridReloadKey] = useState(0);
  const [offCanvasShow, setOffCanvasShow] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [showAlertHistory, setShowAlertHistory] = useState(false);
  const [toastConfig, setToastConfig] = useState<ToastConfig>({
    type: "success" as ErrorTypes,
    title: "",
    icon: "",
  });

  const clinicalAlertReloadKey = useSelector(
    (state: RootState) => state.clinicalAlertReloadKey.clinicalAlertReloadKey,
  );

  const userCache = new Map<string, string>();
  const gridApi = createGridApi();
  const clinicalAlertsApi = createClinicalAlertsApi();

  const preloadUsers = async () => {
    const usersApi = createUsersApi();
    try {
      const response = await usersApi.usersControllerFindAll();
      response.data.forEach(
        (user: { id: string; firstName: string; lastName: string }) => {
          userCache.set(user.id, `${user.firstName} ${user.lastName}`);
        },
      );
      setIsUserCacheLoaded(true);
    } catch (error) {
      console.error("Failed to preload users:", error);
    }
  };

  const reloadGrid = () => {
    setGridReloadKey(gridReloadKey + 1);
  };

  const showToaster = async (toasterConfig: ToastConfig) => {
    setToastConfig(toasterConfig as ToastConfig);
    setShowToast(true);
  };

  useEffect(() => {
    if (!isUserCacheLoaded) {
      preloadUsers();
    }
  }, [isUserCacheLoaded]);

  useEffect(() => {
    if (clinicalAlertReloadKey > 0) {
      reloadGrid();
    }
  }, [clinicalAlertReloadKey]);

  const handleUpdateAlertForm = (event: CellClickedEvent) => {
    setOffCanvasShow(true);
    setSelectedAlertId(event.data.episodeId);
    setSelectedAlertPatientId(event.data.patientId);
  };

  const handleHistoryRenderer = async (
    params: CellClickedEvent | ValueGetterParams,
  ) => {
    try {
      const episodeId = params.data.episodeId;
      const { data } =
        await clinicalAlertsApi.clinicalAlertsControllerGetEpisodeHistoryById(
          episodeId,
        );
      setEpisodeHistoryData(data);
      setShowAlertHistory(true);
    } catch (error) {
      console.error("Error fetching episode history:", error);
    }
  };

  const EditButton = (event: CellClickedEvent) => {
    return (
      <div
        className="d-flex gap-8 align-items-center justify-content-center h-100 cursor-pointer"
        onClick={() => handleUpdateAlertForm(event)}
      >
        <EditIcon />
      </div>
    );
  };

  const HistoryRenderer = (params: CellClickedEvent | ValueGetterParams) => {
    const historyCount = params.data.episodeHistoryCount;
    return (
      <div
        className="d-flex align-items-center justify-content-center h-100 cursor-pointer"
        onClick={() => handleHistoryRenderer(params)}
      >
        <span className="material-symbols-outlined fs-20 text-secondary">
          history
        </span>
        <span>{historyCount}</span>
      </div>
    );
  };

  const [colDefs, setColDefs] = useState<ColDef[]>([
    {
      minWidth: 50,
      maxWidth: 50,
      pinned: "left",
      sortable: false,
      cellRenderer: EditButton,
    },
    {
      headerName: "First Name",
      field: "patientFirstName",
      hide: patientId != undefined,
      minWidth: 120,
      width: 120,
      pinned: "left",
      sortable: true,
    },
    {
      headerName: "Last Name",
      field: "patientLastName",
      hide: patientId != undefined,
      minWidth: 120,
      width: 120,
      pinned: "left",
      sortable: true,
      valueFormatter: (params) => params?.value || "-",
    },
    {
      headerName: "DOB",
      field: "patientDateOfBirth",
      hide: patientId != undefined,
      minWidth: 120,
      maxWidth: 120,
      pinned: "left",
      sortable: true,
      valueFormatter: (params) => {
        return formatDateTask(params?.value || "");
      },
    },
    {
      headerName: "Wellinks ID",
      field: "patientWellinksId",
      hide: patientId != undefined,
      minWidth: 120,
      width: 120,
      pinned: "left",
      sortable: true,
    },
    {
      headerName: "Type",
      field: "episodeTypeName",
      minWidth: 130,
      width: 130,
      sortable: true,
      cellRenderer: (params: ValueFormatterParams) => {
        let badgeClass = "badge";
        switch (params.value) {
          case CommandCenterConstants.EPISODE_TYPE_EMERGENT:
            badgeClass += " status-red-alert"; // red
            break;
          case CommandCenterConstants.EPISODE_TYPE_URGENT:
            badgeClass += " status-yellow-alert text-dark"; // yellow with dark text
            break;
          case CommandCenterConstants.EPISODE_TYPE_NON_URGENT:
            badgeClass += " bg-success"; // green
            break;
          default:
            badgeClass += " bg-light"; // default gray
        }

        return (
          <span
            className={`${badgeClass} rounded-pill fs-12 px-3 py-1 fw-semibold`}
          >
            {params.value}
          </span>
        );
      },
    },
    {
      headerName: "Alert",
      field: "eventTypeName",
      minWidth: 110,
      width: 130,
      sortable: true,
    },
    {
      headerName: "Alert Category",
      field: "episodeCategoryName",
      minWidth: 160,
      width: 160,
      sortable: true,
      valueFormatter: (params) => params?.value || "-",
    },
    {
      headerName: "Status",
      field: "episodeStatusName",
      minWidth: 190,
      width: 190,
      sortable: true,
      cellRenderer: (params: any) => {
        const rollup = params.data?.episodeStatusRollup;
        const name = params.data?.episodeStatusName;

        let dotClass = "bg-secondary"; // default gray
        dotClass = episodeStatusColorMap[name + " " + rollup];
        return (
          <div className="d-flex flex-column">
            <div className="d-flex align-items-center mb-1">
              <span
                className={`me-2 rounded-circle ${dotClass}`}
                style={{
                  width: "10px",
                  height: "10px",
                  display: "inline-block",
                }}
              ></span>
              <span className="fw-semibold text-capitalize">{rollup}</span>
              {name && <span className="fs-11 text-muted ps-1"> ({name})</span>}
            </div>
          </div>
        );
      },
    },
    {
      headerName: "Start Date",
      field: "episodeStartDate",
      minWidth: 180,
      width: 180,
      sortable: true,
      valueFormatter: (params) => {
        return formatDateTimeShort(params?.value);
      },
    },
    {
      headerName: "End Date",
      field: "episodeEndDate",
      minWidth: 180,
      width: 180,
      sortable: true,
      valueFormatter: (params) => {
        return formatDateTimeShort(params?.value);
      },
    },
    {
      headerName: "Days Open",
      field: "daysOpen",
      minWidth: 130,
      width: 130,
      sortable: true,
      valueFormatter: (params) => {
        return `${params?.value} Days`;
      },
    },
    {
      headerName: "Partnership",
      field: "patientPartnerName",
      hide: patientId != undefined,
      minWidth: 200,
      sortable: true,
      valueFormatter: (params) => params?.value || "-",
    },
    {
      headerName: "Created On",
      field: "episodeCreatedAt",
      minWidth: 130,
      width: 130,
      sortable: true,
      valueFormatter: (params) => {
        return formatDateTask(params?.value);
      },
    },
    {
      headerName: "Last Updated",
      field: "episodeUpdatedAt",
      minWidth: 130,
      width: 130,
      sortable: true,
      valueFormatter: (params) => formatDateTask(params?.value) || "-",
    },
    {
      headerName: "Primary Clinician",
      field: "primaryCoachId",
      hide: patientId != undefined,
      minWidth: 130,
      width: 150,
      sortable: true,
      valueFormatter: (params) => userCache.get(params?.value) || "-",
    },
    {
      headerName: "Created By",
      field: "createdByUserId",
      minWidth: 130,
      width: 150,
      sortable: true,
      valueFormatter: (params) => userCache.get(params?.value),
    },
    {
      headerName: "Assignee",
      field: "alertAssigneeId",
      minWidth: 130,
      width: 150,
      sortable: true,
      valueFormatter: (params) => userCache.get(params?.value),
    },
    {
      minWidth: 50,
      maxWidth: 50,
      pinned: "right",
      sortable: false,
      cellRenderer: HistoryRenderer,
    },
  ]);

  const loadData = async (
    request: GridDataRequest,
  ): Promise<GridDataResponse> => {
    const { data } =
      await gridApi.gridControllerGetClinicalAlertsGridData(request);
    return data;
  };

  return (
    <>
      <div className="row">
        <Toaster
          show={showToast}
          onClose={() => setShowToast(false)}
          type={toastConfig.type}
          title={toastConfig.title}
          message={toastConfig.message}
          icon={toastConfig.icon}
        />
      </div>

      <main className="main">
        <CommandCenterHeader
          breadcrumbs={breadcrumbs}
          patientId={patientId}
          reloadGrid={() => setGridReloadKey((prev) => prev + 1)}
          onUpdateFilters={(newFilters) => {
            setFilters(newFilters);
            setInitialFiltersReady(true); // Ensure filters are ready before loading data
          }}
        />
        <div className="d-flex px-3 px-md-4 px-lg-5 d-flex align-items-stretch">
          <div className="flex-fill">
            <div className="py-4">
              {initialFiltersReady && (
                <GenericGrid
                  gridId="command-center-grid"
                  colDefs={colDefs}
                  dataProvider={loadData}
                  filters={filters}
                  key={gridReloadKey}
                />
              )}
            </div>
          </div>
        </div>
      </main>
      <OffCanvas
        title={"Edit Alert"}
        offCanvasShow={offCanvasShow}
        setOffCanvasShow={setOffCanvasShow}
      >
        <AddEditAlert
          patientAlertId={selectedAlertId}
          patientId={selectedAlertPatientId}
          setOffCanvasShow={setOffCanvasShow}
          handleShowToaster={showToaster}
          reloadGrid={reloadGrid}
        />
      </OffCanvas>
      <Modal
        show={showAlertHistory}
        onHide={() => setShowAlertHistory(false)}
        aria-labelledby="contained-modal-title-vcenter"
        centered
      >
        <>
          <Modal.Header closeButton>
            <Modal.Title id="contained-modal-title-vcenter">
              History
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <h3 className="fs-16">
              Alert :{" "}
              {
                episodeHistoryData.find(
                  (history: EpisodeHistoryDTO) =>
                    history.fieldChanged ===
                    CommandCenterConstants.ALERT_HISTORY_CREATED_BY,
                )?.newValue
              }
            </h3>
            {episodeHistoryData?.map(
              (history: EpisodeHistoryDTO, historyIndex: number) => (
                <div key={historyIndex}>
                  <div className="border rounded mt-4">
                    <div className="d-flex align-items-start px-3 py-2 fs-14">
                      <div className="flex-fill">
                        {history.fieldChanged ===
                        CommandCenterConstants.ALERT_HISTORY_CREATED_BY ? (
                          `Alert created from '${history.newValue}'`
                        ) : (
                          <>
                            <strong>Updated {history.fieldChanged} :</strong>{" "}
                            {history.fieldChanged ===
                              CommandCenterConstants.ALERT_HISTORY_START_DATE ||
                            history.fieldChanged ===
                              CommandCenterConstants.ALERT_HISTORY_END_DATE
                              ? `${history.oldValue === "-" ? "-" : formatDateTime(history.oldValue)} -> ${history.newValue === "-" ? "-" : formatDateTime(history.newValue)}`
                              : `${history.oldValue} -> ${history.newValue}`}
                          </>
                        )}
                      </div>
                    </div>
                    <div className="d-flex bg-gray-FAF justify-content-between fs-12 px-3 py-2">
                      <div className="text-light">
                        {formatDateTime(history?.createdAt)}
                      </div>
                      <div className="d-flex align-items-center text-base">
                        <span className="material-symbols-outlined fs-18 me-1">
                          person
                        </span>
                        <span>
                          <UserName userId={history.createdBy} />
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ),
            )}
          </Modal.Body>
        </>
      </Modal>
    </>
  );
};
